import React, { useEffect, useState } from "react";

import { Container, Header } from "./styles";

//Custom components
import ProfileButton from "@components/ProfileButton/ProfileButton";
import OperatorsCard from "./components/OperatorsCard/OperatorsCard";
import DataTable from "@components/DataTable/DataTable";

//Utils
import { tableColumns } from "@resources/tableColumns";
import { arrayHelper } from "@helpers/array";
import { filtersHelper } from "@helpers/filters";
import ReturnsCards from "./components/ReturnsCards/ReturnCards";
import useUser from "@hooks/useUser";
import useReturns from "@hooks/useReturns";
import { Client } from "@domain/models/Client";
import { Return } from "@domain/models/Return";
import { Report, reportResultOptions } from "@domain/models/Report";
import { User } from "@domain/models/User";
import { searchHelper } from "@helpers/search";
import { Route, routeStatus } from "@domain/models/Route";
import { GetReturnClientsReports } from "@useCases/index";
import { HttpReportRepository } from "@data/repositories/index";
import { api } from "@data/api";
import useRoutes from "@hooks/useRoutes";
import PageLoading from "@components/PageLoading/PageLoading";

export type CardsData = {
  completedReturns: Client[];
  pendingReturns: (Client & {
    returnSchedule: Report["returnSchedule"];
  })[];
  inRouteReturns: (Client & { routeStatus: Route["status"]; routeStartAt: Route["startAt"] })[];
  expiredReturns: Client[];
  signedAfterReturn: Client[];
  total: Client[];
};

export type OperatorsData = Partial<User> & CardsData;

function ReturnsPage() {
  const { user, isAdmin } = useUser();

  const { returns } = useReturns();
  const { routesOfDay } = useRoutes();

  const [loading, setLoading] = useState(true);

  //Cards
  const [operatorsData, setOperatorsData] = useState<OperatorsData[]>([]);
  const [cardsData, setCardsData] = useState<CardsData>({
    completedReturns: [],
    pendingReturns: [],
    inRouteReturns: [],
    expiredReturns: [],
    signedAfterReturn: [],
    total: [],
  });

  //filters and search
  const [filters, setFilters] = useState([]);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (returns) {
      processReportsCards(returns);
    }
  }, [returns]);

  useEffect(() => {
    if (cardsData) {
      processOperatorsCard(cardsData);
    }
  }, [cardsData]);

  const processReportsCards = async (returns: Return) => {
    const adminUids = arrayHelper
      .reduceToSimple(user.users.admins, "id")
      .concat([user.users.rootId]);

    if (isAdmin) {
      adminUids.push(user.id);
    }

    const useCase = new GetReturnClientsReports(new HttpReportRepository(api));

    const returnReports = await useCase.execute();

    const completedReturns = returns.completed.filter((x) => !adminUids.includes(x.operatorId));
    const pendingReturns: CardsData["pendingReturns"] = returns.pending
      .concat(returns.premium)
      .filter((x) => !adminUids.includes(x.operatorId))
      .map((item) => {
        const report = returnReports.find((x) => x.clientId === item.id);

        if (report) {
          return {
            ...item,
            returnSchedule: report.returnSchedule,
          };
        } else {
          return {
            ...item,
            returnSchedule: "",
          };
        }
      });

    const expiredReturns = returns.expired.filter((x) => !adminUids.includes(x.operatorId));
    const signedAfterReturn = returns.completed
      .filter(
        (x) =>
          x.lastReportResult === reportResultOptions.SIGNED ||
          x.lastReportResult === reportResultOptions.RECOMMENDATION_SIGNED
      )
      .filter((x) => !adminUids.includes(x.operatorId));
    const total = arrayHelper.ordenateArrayDesc(
      completedReturns.concat(pendingReturns, expiredReturns, signedAfterReturn),
      "lastReportDate"
    );

    const inRouteReturns: CardsData["inRouteReturns"] = total
      .filter((x) => x.currentRouteId)
      .map((item) => {
        const route = routesOfDay.find((x) => x.id === item.currentRouteId);

        if (route) {
          return {
            ...item,
            routeStatus: route.status,
            routeStartAt: route.startAt,
          };
        } else {
          return {
            ...item,
            routeStatus: routeStatus.WAITING,
            routeStartAt: "",
          };
        }
      });

    setCardsData({
      completedReturns: arrayHelper.ordenateArrayDesc(completedReturns, "lastReportDate"),
      pendingReturns: arrayHelper.ordenateArrayDesc(pendingReturns, "lastReportDate"),
      inRouteReturns: arrayHelper.ordenateArrayDesc(inRouteReturns, "lastReportDate"),
      expiredReturns: arrayHelper.ordenateArrayDesc(expiredReturns, "lastReportDate"),
      signedAfterReturn: arrayHelper.ordenateArrayDesc(signedAfterReturn, "lastReportDate"),
      total: arrayHelper.ordenateArrayDesc(total, "lastReportDate"),
    });
  };

  const processOperatorsCard = (data: CardsData) => {
    const array = [];
    const arrayUsers = user.users.operators.filter((x) => x.disabled != true);

    array.push({
      name: "Geral",
      img64: null,
      completedReturns: data.completedReturns,
      pendingReturns: data.pendingReturns,
      inRouteReturns: data.inRouteReturns,
      expiredReturns: data.expiredReturns,
      signedAfterReturn: data.signedAfterReturn,
      total: data.total,
    });

    for (const user of arrayUsers) {
      const findRealized = data.completedReturns.filter((x) => x.operatorId === user.id);
      const findPending = data.pendingReturns.filter((x) => x.operatorId === user.id);
      const findInRoute = data.inRouteReturns.filter((x) => x.operatorId === user.id);
      const findExpired = data.expiredReturns.filter((x) => x.operatorId === user.id);
      const findClosedByReturn = data.signedAfterReturn.filter((x) => x.operatorId === user.id);

      array.push({
        ...user,
        completedReturns: arrayHelper.ordenateArrayDesc(findRealized, "lastReportDate"),
        pendingReturns: arrayHelper.ordenateArrayDesc(findPending, "lastReportDate"),
        inRouteReturns: arrayHelper.ordenateArrayDesc(findInRoute, "lastReportDate"),
        expiredReturns: arrayHelper.ordenateArrayDesc(findExpired, "lastReportDate"),
        signedAfterReturn: arrayHelper.ordenateArrayDesc(findClosedByReturn, "lastReportDate"),
        total: arrayHelper.ordenateArrayDesc(
          findRealized.concat(findPending, findInRoute, findExpired, findClosedByReturn),
          "lastReportDate"
        ),
      });
    }

    setOperatorsData(array);

    setTimeout(() => {
      setLoading(false);
    }, 4000);
  };

  const filterByTable = () => {
    if (cardsData?.total) {
      return filtersHelper.filterDataTable(cardsData?.total, filters);
    } else {
      return [];
    }
  };

  const clearFilters = () => {
    setFilters([]);
    setSearch("");
  };

  const baseTableData = filterByTable();

  const backupTableData = cardsData ? cardsData?.total : [];

  const tableData = search
    ? searchHelper.searchTable(search, tableColumns.returns, backupTableData)
    : baseTableData;

  if (loading) {
    return (
      <Container>
        <PageLoading />
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <h1>Central de Retornos</h1>
        <ProfileButton arrowColor="var(grey2)" />
      </Header>

      <ReturnsCards cardsData={cardsData} />

      <OperatorsCard operatorsData={operatorsData} />

      <DataTable
        title="Clientes com retorno"
        tableColumns={tableColumns.returns}
        tableRows={tableData}
        tableRowsBackup={backupTableData}
        filterTypes={tableColumns.returns}
        columns={7}
        filtersHighlights={[
          {
            field: "lastReportDate",
            headerName: "Data",
          },
          {
            field: "operatorName",
            headerName: "Operador",
          },
          {
            field: "lastReportResult",
            headerName: "Resultado",
          },
        ]}
        perPage={15}
        dateField="lastReportDate"
        itemType="return"
        search={search}
        setSearch={setSearch}
        filters={filters}
        onChangeFilters={(newFiltersArray) => {
          setFilters(newFiltersArray);
        }}
        onClearFilters={() => clearFilters()}
        onClick={() => {}}
      />
    </Container>
  );
}

export default ReturnsPage;
